// src/store/useAuthStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { axiosInstance } from "../lib/axiosInstance"; // Assuming v2 is the default instance

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      userType: null, // 'builder' or 'customer'
      token: null,
      isSigningUp: false,
      isCheckingAuth: false,

      signupBuilder: async (builderData) => {
        set({ isSigningUp: true });
        try {
          const formData = new FormData();
          Object.keys(builderData).forEach((key) => {
            if (
              builderData[key] !== "" &&
              builderData[key] !== null &&
              builderData[key] !== undefined
            ) {
              if (key === "verifiedAadhaarData" || key === "verifiedGstinData") {
                // Stringify the verification data objects
                formData.append(key, JSON.stringify(builderData[key]));
              } else {
                formData.append(key, builderData[key]);
              }
            }
          });

          // Log FormData contents for debugging (optional)
          for (let pair of formData.entries()) {
            console.log(pair[0]+ ', ' + pair[1]); 
          }

          const res = await axiosInstance.post(
            "/builders/auth/signup",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          // Assuming the signup response includes the builderUniqueId
          // If not, this part needs adjustment or rely on login for builderUniqueId
          // For now, we'll just return success and let login handle setting user details.
          return { success: true, message: "Account created successfully. Please login." };
        } catch (error) {
          console.error("Signup Error:", error);
          const errorMsg = error?.response?.data?.error || error?.response?.data?.message || "Signup failed";
          toast.error(errorMsg);
          return { success: false, message: errorMsg };
        } finally {
          set({ isSigningUp: false });
        }
      },

      customerSignup: async (name, email, password) => {
        try {
          const response = await axiosInstance.post("/customer/signup", {
            name,
            email,
            password,
          });
          toast.success("Customer Signup Successful! Please login.");
          return true;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Customer Signup Failed";
          toast.error(errorMsg);
          return false;
        }
      },

      loginBuilder: async (credential) => {
        try {
          const response = await axiosInstance.post("/builders/auth/login", credential);
          const token = response.data.token;
          localStorage.setItem("builderToken", token);
          const tokenPayload = JSON.parse(atob(token.split(".")[1]));

          // Store more comprehensive user info, including builderUniqueId
          set({
            user: {
              id: tokenPayload.builderId, // Numeric ID
              builderUniqueId: tokenPayload.builderUniqueId, // String ID like DTCHE000X
              email: tokenPayload.email,
              name: tokenPayload.name,
              // Add other relevant fields from tokenPayload if needed
            },
            userType: "builder",
            token: token,
          });

          toast.success("Builder Login Successfully");
          window.location.href = '/builder/dashboard';
          return true;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Builder Login Failed";
          toast.error(errorMsg);
          throw error;
        }
      },

      customerLogin: async (credential) => {
        try {
          const response = await axiosInstance.post("/customer/login", credential);
          const token = response.data.token;
          localStorage.setItem("customerToken", token);

          const tokenPayload = JSON.parse(atob(token.split(".")[1]));

          set({
            user: {
              name: response.data.name, // Name from response body
              email: response.data.email, // Email from response body
              customerId: tokenPayload.customerId, // From token
              userId: tokenPayload.userId // From token
            },
            userType: "customer",
            token: token,
          });
          toast.success("Customer Login Successfully");
          window.location.href = '/customer/projectinvite';
          return true;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Customer Login Failed";
          toast.error(errorMsg);
          throw error;
        }
      },

      setGoogleAuth: (userData, token) => {
        localStorage.setItem("customerToken", token);
        set({
          user: { // Ensure this structure matches other login methods if possible
            name: userData.name,
            email: userData.email,
            customerId: userData.customerId, // This field is crucial
            userId: userData.id
          },
          userType: 'customer',
          token: token
        });
        toast.success("Logged in with Google successfully!");
      },

      logoutBuilder: () => {
        if (get().userType === "builder") {
          localStorage.removeItem("builderToken");
          set({ user: null, userType: null, token: null });
          toast.success("Builder logged out successfully");
        }
      },

      logoutCustomer: () => {
        if (get().userType === "customer") {
          localStorage.removeItem("customerToken");
          set({ user: null, userType: null, token: null });
          toast.success("Customer logged out successfully");
        }
      },

      forgotPassword: async (email) => {
        try {
          const response = await axiosInstance.post('/builders/auth/forgot-password', { email });
          if (response.data.success) {
            return true;
          } else {
            toast.error(response.data.message || 'Failed to send reset email.');
            return false;
          }
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Error sending password reset email.";
          toast.error(errorMsg);
          throw error;
        }
      },

      verifyResetToken: async (token) => {
        if (!token) return false;
        try {
          const response = await axiosInstance.get(`/builders/auth/verify-reset-token/${token}`);
          return response.data.success === true;
        } catch (error) {
          console.error("Token verification failed:", error.response?.data?.message || error.message);
          return false;
        }
      },

      resetPassword: async (token, newPassword) => {
        try {
          const response = await axiosInstance.post('/builders/auth/reset-password', { token, newPassword });
          if (response.data.success) {
            return true;
          } else {
            toast.error(response.data.message || 'Failed to reset password.');
            return false;
          }
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Error resetting password.";
          toast.error(errorMsg);
          throw error;
        }
      },

    }),
    {
      name: "auth-storage",
      getStorage: () => localStorage,
    }
  )
);

export default useAuthStore;